import {
  type FinalWordData,
  WordCloud as ReactWordCloud,
} from '@isoterik/react-word-cloud';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { CenterFlex, Flex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import { Stage } from '@/components/Stage/Stage';
import useColor from '@/hooks/domain/useColor';

import type { WordCloudBasis, WordCloudItem } from '../types';
import { scaleWordCloudValues } from '../utils';

interface Props {
  words: WordCloudItem[];
  onWordClick: (word: FinalWordData) => void;
  css?: StyledCss;
  isLoading?: boolean;
  wordCloudId?: string;
  basis?: WordCloudBasis;
}

const WordCloud = memo(
  ({ words, css, onWordClick, isLoading = false, wordCloudId, basis }: Props) => {
    const color = useColor();
    const { wordCloudSize, setRef } = useResponsiveWordCloud();

    const minScale = Math.floor(
      (wordCloudSize.width * wordCloudSize.height) / 750,
    );
    const maxScale = (wordCloudSize.width * wordCloudSize.height) / 100;

    const scaledWords = scaleWordCloudValues(words, minScale, maxScale, basis);

    const scaledPadding = 5;

    const getColor = (value: number) => {
      if (value > maxScale * 0.8) {
        return color.gray800;
      }
      if (value > maxScale * 0.5) {
        return color.gray600;
      }
      if (value > maxScale * 0.3) {
        return color.gray500;
      }

      return color.gray400;
    };

    return (
      <StyledWordCloudContainer
        css={css}
        f={1}
        ref={setRef}
        data-wordcloud-id={wordCloudId}
      >
        <Stage className="w-full h-full flex items-center justify-center">
          <Stage.Scene status={isLoading}>
            <CenterFlex f={1}>
              <Spinner />
            </CenterFlex>
          </Stage.Scene>
          <Stage.Scene status={scaledWords.length === 0}>
            <Empty
              title="No data available"
              description="There is currently no data available to generate a word cloud."
            />
          </Stage.Scene>
          <Stage.Scene status={scaledWords.length > 0}>
            <ReactWordCloud
              words={scaledWords}
              width={wordCloudSize.width}
              height={wordCloudSize.height}
              fontWeight={600}
              rotate={() => 0}
              padding={scaledPadding}
              font="pretendard"
              spiral="archimedean"
              fill={(word: FinalWordData) => {
                return getColor(word.value);
              }}
              onWordClick={onWordClick}
            />
          </Stage.Scene>
        </Stage>
      </StyledWordCloudContainer>
    );
  },
);

const useResponsiveWordCloud = () => {
  const [wordCloudSize, setWordCloudSize] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateSize = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setWordCloudSize((currentSize) => {
        if (currentSize.width === width && currentSize.height === height) {
          return currentSize;
        }
        return { width, height };
      });
    }
  }, []);

  const debouncedUpdateSize = useDebouncedCallback(updateSize, 200);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      // 기존 observer 정리
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      if (el) {
        containerRef.current = el;

        // 초기 크기 설정
        updateSize();

        // ResizeObserver 설정
        const observer = new ResizeObserver(() => {
          debouncedUpdateSize();
        });

        resizeObserverRef.current = observer;
        observer.observe(el);
      } else {
        containerRef.current = null;
      }
    },
    [updateSize, debouncedUpdateSize],
  );

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 observer 정리
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  return {
    wordCloudSize,
    setRef,
  };
};

export { WordCloud };

const StyledWordCloudContainer = styled(Flex)`
  height: 100%;
  overflow: hidden;
  text {
    paint-order: stroke fill;
    cursor: pointer;
    transition: all 0.2s;
    stroke-width: 0.15em;
    stroke: transparent;

    &:hover {
      fill: ${({ theme }) => theme.colors.primary100} !important;
      stroke: ${({ theme }) => theme.colors.primaryAlpha(0.1)};
    }
  }
`;
